<script>
import getGoogleContacts from '@utils/people-api';
import ImportContacts from '@components/prospects/import-contacts.vue';
import { prospectComputed, prospectMethods } from '@state/helpers';
import { mask } from 'vue-the-mask';
export default {
  components: {
    ImportContacts,
  },
  directives: { mask },
  data() {
    return {
      contactApiError: false,
      step: 1,
      importProcess: null,
      areProspectsValid: false,
      loadingImportProspects: false,
      importLoader: false,
      newProspect: {
        first: '',
        last: '',
        phone: '',
        email: '',
      },
      asideText: [
        {
          title: ['Pick A Source', 'Select Leads'],
          paragraph: [
            'Finding folks that want to train with you has never been easier. Sync your contacts to automatically add friends and family to your lead list to let them know you are ready to train them',
            'To get started, select leads to send a campaign to. We recommend at least 15 leads, as not everyone will reply to your first reach out!',
          ],
        },
        {
          title: ['Why Is This Important?', 'Why So Many?'],
          paragraph: [
            'Lorem ipsum dolor sit amet consectetur.',
            'On average, only 10-20% of leads will reply! Increasing the number of leads you reach out to increases the chances of new clients in the long run!',
          ],
        },
      ],
    };
  },
  computed: {
    ...prospectComputed,
    conflictsWithAProspect() {
      return this.contacts.some((contact) => contact.conflicts?.length);
    },
    browserHasCookies() {
      return !!navigator.cookieEnabled;
    },
  },
  methods: {
    ...prospectMethods,
    // @step 1
    async importGoogleContacts() {
      this.importProcess = 'google';
      this.importLoader = true;
      this.step++;
      await getGoogleContacts();

      // check if success
      if (!this.contacts) {
        this.step--;
        this.contactApiError = true;
      }

      this.importLoader = false;
    },
    importManually() {
      this.importProcess = 'manually';
      this.step++;
    },

    // @step 2
    addProspect() {
      const { first, last, phone, email } = this.newProspect;
      this.setContacts({
        selected: true,
        firstName: first,
        lastName: last,
        phone: [phone],
        email: [email],
      });
      this.newProspect.first = '';
      this.newProspect.last = '';
      this.newProspect.phone = '';
      this.newProspect.email = '';
    },
    cancel() {
      this.$bvModal.hide('import-wizard-modal');
      this.setContacts();
      this.step--;
      this.contactApiError = false;
    },
    async addContactsAndClose() {
      if (this.areProspectsValid === false) {
        return false;
      }
      this.loadingImportProspects = true;
      this.setInitialImport(false);
      const response = await this.verifyProspects(
        this.contacts.filter((contact) => contact.selected)
      );
      this.loadingImportProspects = false;
      response && this.cancel();
    },
    async addContactsAndInvite() {
      if (this.areProspectsValid === false) {
        return false;
      }
      this.loadingImportProspects = true;
      this.setInitialImport(false);
      const response = await this.verifyProspects(
        this.contacts.filter((contact) => contact.selected)
      );
      this.loadingImportProspects = false;
      if (response) {
        this.cancel();
        this.programaticallyClickReachOutButton();
      }
    },
    programaticallyClickReachOutButton() {
      const button = document.getElementsByClassName('btn-primary')[1];
      button.click();
    },
  },
};
</script>

<template>
  <b-modal
    id="import-wizard-modal"
    centered
    :hide-footer="true"
    :hide-header="true"
    size="xl"
    scrollable
  >
    <div class="flex">
      <div class="aside">
        <article>
          <h3>{{ asideText[0].title[step - 1] }}</h3>
          <p>{{ asideText[0].paragraph[step - 1] }}</p>
        </article>
        <article class="secondary-article">
          <h4>{{ asideText[1].title[step - 1] }}</h4>
          <p>{{ asideText[1].paragraph[step - 1] }}</p>
        </article>
      </div>
      <div class="main">
        <div class="dedupe-alert" v-if="step === 2 && conflictsWithAProspect">
          It looks like some of these leads are already in our system. Remove or
          change highlighted items.
        </div>
        <div class="header-section">
          <div :class="{ selected: step === 1 }">
            <span>1</span>
            <p>Select Source</p>
          </div>
          <div :class="{ selected: step === 2 }">
            <span>2</span>
            <p>Select Leads</p>
          </div>
        </div>
        <div class="main-section">
          <template v-if="step === 1">
            <p v-if="contactApiError" class="error">
              There was an error connecting with Google. Try again or input
              leads manually.
            </p>
            <div class="import-methods">
              <button
                :class="['google', { disabled: !browserHasCookies }]"
                @click.shift="importGoogleContacts"
                :disabled="!browserHasCookies"
              >
                <svg width="23" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M22.875 12.281c0-.75-.094-1.312-.188-1.922H11.626v3.985h6.563c-.235 1.734-1.97 5.015-6.563 5.015-3.984 0-7.219-3.28-7.219-7.359 0-6.516 7.688-9.516 11.813-5.531l3.187-3.047C17.391 1.547 14.72.375 11.625.375A11.593 11.593 0 000 12a11.564 11.564 0 0011.625 11.625c6.703 0 11.25-4.688 11.25-11.344z" fill="#fff"/></svg>
                <span>Use Google Address Book</span>
              </button>
              <p v-if="!browserHasCookies" class="cookie-error"
                >Google import doesn't work in incognito</p
              >
              <button class="manual" @click.stop="importManually">
                <span>Import Leads Manually</span>
              </button>
            </div>
          </template>
          <template v-if="step === 2">
            <ImportContacts
              @are-prospect-valid="areProspectsValid = $event"
              :importLoader="importLoader"
            />
            <p
              v-if="emptyContacts && importProcess === 'google'"
              class="message-first-prospect"
            >
              Connect your Google account to get started. Check and make sure
              your browser didn't block the popup.
            </p>
            <p v-else-if="emptyContacts" class="message-first-prospect">
              Get started by adding your first lead below.
            </p>
          </template>
        </div>
        <template v-if="step === 2">
          <div class="row px-0 w-100">
            <div class="col align-self-end px-0">
              <form
                :class="[
                  'additional-prospects',
                  { 'no-contacts': emptyContacts, error: false },
                ]"
                @submit.prevent="addProspect"
              >
                <input
                  placeholder="First Name"
                  v-model="newProspect.first"
                  class="input_first_name"
                />
                <input
                  placeholder="Last Name"
                  v-model="newProspect.last"
                  class="input_last_name"
                />
                <input
                  placeholder="Phone Number"
                  v-model="newProspect.phone"
                  v-mask="'+1 (###) ###-####'"
                  class="input_phone"
                />
                <input
                  placeholder="Email Address"
                  v-model="newProspect.email"
                  class="input_email"
                />
                <button type="submit">Add Lead</button>
              </form>
              <div class="footer-section">
                <div class="close" @click.stop="cancel"><p>Cancel</p></div>
                <div
                  :class="['select-prospect', { 'no-contacts': emptyContacts }]"
                >
                  <p
                    :disabled="areProspectsValid === false"
                    :class="{
                      'no-contacts':
                        emptyContacts || areProspectsValid === false,
                    }"
                    @click.stop="addContactsAndClose"
                    >Select & Save</p
                  >
                  <BaseButton
                    :disabled="areProspectsValid === false"
                    @click.stop="addContactsAndInvite"
                    type="button"
                    :class="[
                      'btn-primary',
                      'btn-round',
                      'invite',
                      {
                        'no-contacts':
                          emptyContacts || areProspectsValid === false,
                      },
                    ]"
                  >
                    <span>Start Campaign</span>
                  </BaseButton>
                </div>
              </div>
            </div>
          </div>
        </template>
        <div class="loading-wrapper" v-if="loadingImportProspects">
          <BaseLoadingSpinner />
        </div>
      </div>
    </div>
  </b-modal>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown
// stylelint-disable no-descending-specificity
// stylelint-disable selector-max-type

.flex {
  display: flex;
  justify-content: space-between;
  width: 100%;

  .aside,
  .main {
    align-items: center;
    display: flex;
    flex-direction: column;
  }

  .aside {
    background: $color-off-white-four;
    border: 1px solid $color-light-gray-fourteen;
    border-radius: 16px 0 0 16px;
    box-sizing: border-box;
    justify-content: center;
    padding: 8em 3.5em;
    width: 28%;

    .secondary-article {
      background: $color-white;
      border-radius: 8px;
      margin-top: 2em;
      padding: 1em 2em;
    }

    p {
      color: $color-black;
      font-size: 1.7rem;
      font-weight: normal;
      letter-spacing: -0.25px;
      line-height: 28px;
    }

    h3 {
      margin-bottom: 0.75em;
    }
  }

  .main {
    position: relative;
    width: 72%;
    .dedupe-alert {
      background: $color-yellow;
      color: $color-black;
      font-size: 1.4rem;
      font-weight: 300;
      letter-spacing: -0.25px;
      line-height: 24px;
      margin-top: 5px;
      padding: 15px 30px;
      position: absolute;
      width: 97%;
    }

    .header-section,
    .footer-section {
      display: flex;
      width: 100%;
    }
    .header-section {
      align-items: center;
      border-bottom: 1px solid rgba($color-black-wo-rgb, 0.2);
      top: 0;

      div {
        align-items: center;
        display: flex;
        font-size: 1.4rem;
        justify-content: center;
        padding: 2em 0;
        text-align: center;
        width: 100%;

        span {
          align-items: center;
          border: 2px solid $color-cohort-gray;
          border-radius: 50%;
          box-sizing: border-box;
          color: $color-cohort-gray;
          display: inline-flex;
          font-weight: 900;
          height: 30px;
          justify-content: center;
          letter-spacing: -0.25px;
          line-height: 14px;
          margin-right: 1em;
          width: 30px;
        }

        p {
          color: $color-black;
          font-weight: 300;
          letter-spacing: -0.25px;
          line-height: 24px;
        }

        &.selected {
          background: rgba($color-action-wo-rgb, 0.2);
          border-bottom: 2px solid $color-action;

          span {
            background: $color-action;
            border: 2px solid $color-action;
            color: $color-white;
          }

          p {
            color: $color-action;
            font-weight: bold;
          }
        }
      }
    }
    .footer-section {
      justify-content: space-between;
      padding: 1.5em 3em;

      p {
        cursor: pointer;
        font-size: 1.7rem;
        font-weight: bold;
        line-height: 12px;
      }

      .close {
        align-self: center;
        p {
          color: $color-black-one;
          opacity: 0.5;
        }
      }

      .select-prospect {
        align-items: center;
        display: flex;

        .invite {
          margin-left: 2em;
          &.no-contacts {
            opacity: 0.5;
          }
        }

        p {
          color: $color-action;
          line-height: 12px;
          &.no-contacts {
            opacity: 0.5;
          }
        }
      }
    }
    p {
      margin-bottom: 0;
    }

    .main-section {
      height: 100%;
      overflow: scroll;
      width: 100%;
      table {
        min-width: 750px;
        width: 100%;
      }
      .message-first-prospect,
      .error {
        font-size: 2.1rem;
        font-weight: 900;
        letter-spacing: -0.25px;
        line-height: 24px;
        margin: 0 auto;
        text-align: center;
      }
      .message-first-prospect {
        color: $color-black-one;
        max-width: 40%;
        padding-top: 1em;
      }

      .error {
        color: $color-red-one;
        max-width: 65%;
        padding-top: 3em;
      }

      .import-methods {
        align-items: center;
        display: flex;
        flex-direction: column;
        padding: 3em 0;

        button + button {
          margin-top: 2em;
        }

        span {
          color: $color-white;
          font-size: 1.7rem;
          font-weight: bold;
          letter-spacing: -0.25px;
          line-height: 28px;
          margin-left: 20px;
          text-transform: capitalize;
          white-space: nowrap;
        }

        .google,
        .manual {
          border: none;
          border-radius: $base-border-radius-md;
          cursor: pointer;
          display: flex;
          justify-content: center;
          max-width: 300px;
          padding: 2em;
          width: 100%;
        }

        .google {
          background: $color-red-one;
          &.disabled {
            background-color: grey;
            cursor: not-allowed;
            opacity: 0.5;
          }
        }
        .manual {
          background: $color-action;
        }
        .cookie-error {
          color: $color-red-one;
        }
      }
    }

    .additional-prospects {
      align-items: center;
      background: $color-off-white-three;
      box-shadow: 0 -1px 0 rgba($color-black-wo-rgb, 0.25);
      display: flex;
      padding: 1em;
      width: 100%;
      button {
        background: inherit;
        border: none;
        color: $color-action;
        font-size: 1.2rem;
        letter-spacing: -0.25px;
        line-height: 16px;
        margin-left: 0.75em;
        text-transform: capitalize;
      }

      input {
        background: $color-white;
        border: 1px solid $color-light-gray-sixteen;
        box-sizing: border-box;
        line-height: 1;
        padding: 0.75em;
        &.input_first_name {
          width: 15%;
        }
        &.input_last_name {
          width: 15%;
        }
        &.input_phone {
          width: 20%;
        }
        &.input_email {
          width: 30%;
        }
      }
      // to add margin between items
      input + input {
        margin-left: 0.75em;
      }

      &.no-contacts {
        background: $color-primary-light;
        box-shadow: 0 -1px 0 rgba($color-primary-one-wo-rgb, 0.5);
      }
    }
    .loading-wrapper {
      background: rgba($color-white-wo-rgb, 0.5);
      bottom: 0;
      left: 0;
      position: absolute;
      right: 0;
      top: 0;
      .loading {
        margin: 0 auto;
        position: relative;
        top: 50%;
      }
    }
  }
}
::v-deep .modal-content {
  background: $color-white;
  border: 1px solid $color-light-gray-fourteen;
  border-radius: 16px;
  box-shadow: 0 0 58px rgba($color-black-wo-rgb, 0.12);
  box-sizing: border-box;
  min-height: 80vh;

  .modal-body {
    padding: 0;
  }
}
::v-deep .modal-dialog {
  @media (min-width: 600px) {
    margin: 1em auto;
    max-width: 90%;
    width: 1230px;
    .modal-content {
      max-height: 700px;
    }
  }
}
</style>
